import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  IconButton,
  Grid,
  Box,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { Formik, Form } from "formik";
import DatePickerComponent from "../../../../../../../common/components/date-picker/date-picker-compoenent";
import {
  AlertComponent,
  TextInputComponent,
} from "../../../../../../../common/components";
import {
  CALL_DIALOG_INITIAL_VALUES,
  CALL_PURPOSE_OPTIONS,
  CALL_DIALOG_VALIDATION_SCHEMA_WITTHOUT_IS_LOG_CALL,
  getFullName
} from "./common-dialog-helper";
import SearchableSelectOwner from "../../../../../../../common/components/select-owner/select-owner-compoent";
import { getCurrentUser } from "../../../../../../../common/routines/user-operations";
import SelectInputComponent from "../../../../../../../common/components/select-input-component/select-input-component";
import dayjs from "dayjs";
import "./common-dialog-task.scss";
import "./common-dialog-call.scss";
import { getUserDetails } from "../../../../../../../api/lead";
import { useSetLoading } from "../../../../../../../common/hooks/appearance";
import SearchableAutoSelect from "../../../../../../../common/components/auto-select-search-component/search-select-component";
import { TimePicker } from "@mui/x-date-pickers";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import lockIcon from "../../../../../../../assets/icons/lock-icon.svg";
import { getCombinedDateTime } from "../../../../../../../utils/utils";
import { ConfirmationPopup } from "../../../../../../../common/components";
import ConfirmInfo from "../../../../../../../assets/icons/CreateLeadCapturePopupCancelIcon.svg";
import MultiSelectAutocompleteWithProfile from "../../../../../../leads/lead-assignment/assignement-rules/multi-option-select-with-profile";
import {
  createCall,
  editCall,
} from "../../../../../../../api/campaign";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { DesktopTimePicker } from '@mui/x-date-pickers/DesktopTimePicker';

interface CommonCallDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (values: any) => void;
  title?: string;
  leadDetails?: any;
  initialCall?: any;
  isLogCall?: boolean;
  customerOptions?: any[]; // This prop is not used in the component, but can be passed if needed
  markCallAsCompletedOpen?: boolean;
}
export interface OwnerOption {
  value: string;
  label: string;
  email: string;
  avatar?: string | null;
}
const CommonCallDialog: React.FC<CommonCallDialogProps> = ({
  open,
  onClose,
  onSave,
  leadDetails,
  title = "Schedule Call",
  initialCall,
  customerOptions = [],
}) => {
  const [leadOptions, setLeadOptions] = useState<OwnerOption[]>([]);
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertType, setAlertType] = useState<"success" | "error">("success");
  const [loading, setLoading] = useState<boolean>(false);
  const [formInitialValues, setFormInitialValues] = useState(
    CALL_DIALOG_INITIAL_VALUES
  );
  const [isConflictWithMeeting, setIsConflictWithMeeting] = useState(false);
  const [conflictMessage, setConflictMessage] = useState("");
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm")); // mobile ≤ 600px


  useEffect(() => {
    setIsConflictWithMeeting(false);
  }, [open]);

  const handleAlertClose = () => {
    setAlertOpen(false);
  };

  const fetchCallStatus = () => {

    const isOverdue = dayjs(initialCall?.call_start_time).isBefore(dayjs());
    if (initialCall?.out_going_call_status !== "Completed" && isOverdue) { 
      return "Overdue";
    }
    return initialCall?.out_going_call_status || "Scheduled";
  }

  const fullName=getFullName(leadDetails?.first_name,leadDetails?.middle_name,leadDetails?.last_name);
  

  const computedInitialValues = useMemo(() => {
    let values;

    if (initialCall) {
      values = {
        callFor: initialCall.callFors?.length > 0 ? initialCall.callFors[0]?.fullName : "",
        callType: initialCall.call_type || "",
        outGoingCallStatus: initialCall.outgoing_call_status ? fetchCallStatus() : "",
        callStartTime: initialCall.call_start_time
          ? dayjs(initialCall.call_start_time)
          : null,
        callStartDate: initialCall.call_start_time
          ? dayjs(initialCall.call_start_time)
          : null,
        callOwner: initialCall.callOwner?.id ? String(initialCall.callOwner.id) : "",
        subject: initialCall.subject || "",
        callAgenda: initialCall.call_agenda || "",
        callPurpose: initialCall.call_purpose || "",
      };
    } else {
      const currentUserId = getCurrentUser();
      const matchedUser = leadOptions.find(
        (opt) => opt.value === String(currentUserId?.id)
      );

      values = {
        ...CALL_DIALOG_INITIAL_VALUES,
        callFor: customerOptions?.length > 0 ? customerOptions[0]?.label : "",
        callOwner: matchedUser?.value || "",
        relatedTo: fullName || "",
        outGoingCallStatus: "Scheduled",
      };
    }


    return values;
  }, [initialCall, leadDetails, leadOptions]);
  

  const [pendingPayload, setPendingPayload] = useState<any>(null);

  const padTime = (val: string | number) => String(val).padStart(2, "0");


  const preparePayload = (values: any) => {
    const payload: any = {
      callFor: customerOptions.length > 0 ? [{ id: customerOptions[0]?.value, resource: "lead" }] : [],
      relatedToLeadId: leadDetails?.id || null,
      callType: values.callType || null,
      outGoingCallStatus: values.outGoingCallStatus,
      callStartTime: getCombinedDateTime(
        values.callStartDate,
        values.callStartTime
      ),
      ownerId: Number(values.callOwner),
      subject: values.subject || null ,
      callPurpose: (values.callPurpose === "None" ? null : values.callPurpose) || null,
      callAgenda: values.callAgenda || null,
      notes: initialCall?.notes || null, // Use initialCall?.notes if available
      cancelNotes: initialCall?.cancel_notes || null, // Use initialCall?.cancel_notes if available
    };


    return payload;
  };

  const saveCall = async (payload: any, originalValues?: any) => {
    try {
      setLoading(true);
      let response;
      if (initialCall?.id) {
        response = await editCall(initialCall.id, payload);
        setAlertMessage("Call updated successfully.");
      } else {
        response = await createCall(payload);
        setAlertMessage("Call created successfully.");
      }
      setAlertType("success");
      setAlertOpen(true);
      onSave(response?.data || originalValues);
      // onClose();
    } catch (error: any) {
      console.error("Error saving call:", error);
      if (error?.response?.status === 409) {
        setPendingPayload(payload); // Store for re-use
        setIsConflictWithMeeting(true);
        setConflictMessage(
          error?.response?.data?.message ||
            "This call conflicts with an existing meeting or call. Do you want to proceed?"
        );
      } else {
        setAlertMessage(
          error?.response?.data?.message ||
            "Failed to save call. Please try again."
        );
        setAlertType("error");
        setAlertOpen(true);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (values: any) => {
    const payload = preparePayload(values);
    await saveCall(payload, values);
  };

  useEffect(() => {
    const fetchCreatedByOptions = async () => {
      try {
        const response = await getUserDetails({}, "Create Campaigns");
        const data = response.data.data.data.data;
        const formattedOptions: OwnerOption[] = data.map((user: any) => ({
          value: String(user.id),
          label: `${user.first_name} ${user.last_name}`,
          email: user.email,
          avatar: user.profile_image_url || null,
        }));

        setLeadOptions(formattedOptions);

        // Set default campaign owner
        const currentUserId = getCurrentUser();
        const matchedUser = formattedOptions?.find(
          (opt) => opt.value === String(currentUserId?.id)
        );
        if (matchedUser) {
          setFormInitialValues((prev: any) => ({
            ...prev,
            callOwner: matchedUser.value,
            relatedTo: leadDetails?.name || "",
          }));
        }
      } catch (error: any) {
        setAlertMessage(
          error?.response?.data?.message ||
            "Something went wrong please try again."
        );
        setAlertType("error");
        setAlertOpen(true);
        setLoading(false);
      }
    };

    fetchCreatedByOptions();
  }, [leadDetails]);

  const roundToNext15Min = (time: dayjs.Dayjs) => {
    const minutes = time.minute();
    const remainder = 15 - (minutes % 15);
    return time.add(remainder, "minute").startOf("minute");
  };

  const getMinSelectableTime = (values: any) => {
    const now = dayjs();
    const selectedDate = dayjs(values?.callStartDate);

    if (selectedDate.isSame(now, "day")) {
      const rounded = roundToNext15Min(now);

      // Return a Day.js object with only time info (arbitrary fixed date)
      return dayjs()
        .startOf("day")
        .hour(rounded.hour())
        .minute(rounded.minute())
        .second(0);
    }

    return dayjs().startOf("day");
  };

  

  const showLoading = useMemo(() => loading, [loading]);
  useSetLoading(showLoading);

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        maxWidth="sm"
         slotProps={{
          paper: {
            sx: {
              width: isMobile ? "90%" : "650px",   // 90% width on mobile
              minWidth: isMobile ? "auto" : "640px",
              position: isMobile ? "fixed" : "absolute",
              top: 0,
              margin: 0,
            },
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: 1,
            paddingLeft: 3,
            borderBottom: "1px solid #E1E1E1",
          }}
        >
          <Typography
            variant="h6"
            sx={{ fontWeight: "bold !important" }} // You can also use 700 or 'bold'
          >
            {title}
          </Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <Formik
          enableReinitialize
          initialValues={computedInitialValues}
          validationSchema={CALL_DIALOG_VALIDATION_SCHEMA_WITTHOUT_IS_LOG_CALL}
          onSubmit={(values) => handleSave(values)}
      
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            setFieldValue,
            setFieldTouched,
          }) => {
            return (
              <Form>
                <div className="task-dialog-container task-dialog-call-container">
                  <DialogContent>
                    <Grid container spacing={2}>
                      <Typography className="call-info-header">
                        Call Information
                      </Typography>
                      {/* Call For */}
                      <Grid item xs={12}>
                        <Box display="flex" alignItems="center" gap={6}>
                          <Typography width={isMobile ? 100 : 200}>Call For</Typography>
                          <Box flex={1} className="call-for-wrapper disable-dropdown call-type-input">
                            <TextInputComponent
                              type="text"
                              fieldName="callFor"
                              placeholder="Call For"
                              optional={false}
                              disabled={true}
                              value={values.callFor || ""}
                            />
                            {errors.callFor &&
                              touched?.callFor &&
                              typeof errors.callFor === "string" && (
                                <div className="error-message">
                                  {errors.callFor}
                                </div>
                              )}
                          </Box>
                        </Box>
                      </Grid>

                      {/* Related To */}

                      {/* Call Type */}
                      <Grid item xs={12}>
                        <Box display="flex" alignItems="center" gap={6}>
                          <Typography width={isMobile ? 100 : 200}>Call Type</Typography>
                          <Box
                            flex={1}
                            className="call-type-input static-field"
                          >
                            <TextInputComponent
                              type="text"
                              fieldName="callType"
                              placeholder="Call Type"
                              optional={false}
                              disabled={true}
                              value={values.callType}
                              // onChange={handleChange}
                              maxlength="200"
                              isLocked={true} // Locking the field
                            />
                            { (
                              <Box className="lock-icon-wrapper">
                                <img
                                  src={lockIcon}
                                  alt="lock icon"
                                  style={{ width: 20, height: 20 }}
                                />
                              </Box>
                            )}
                          </Box>
                        </Box>
                      </Grid>

                      {/* Outgoing Call Status */}
                      <Grid item xs={12}>
                        <Box display="flex" alignItems="center" gap={6}>
                          <Typography width={isMobile ? 100 : 200}>
                            Outgoing Call Status
                          </Typography>
                          <Box flex={1} className="static-field">
                            <TextInputComponent
                              type="text"
                              fieldName="outGoingCallStatus"
                              optional={false}
                              disabled={true}
                              value={values.outGoingCallStatus}
                              maxlength="200"
                            />
                            <Box className="lock-icon-wrapper">
                              <img
                                src={lockIcon}
                                alt="lock icon"
                                style={{ width: 20, height: 20 }}
                              />
                            </Box>
                          </Box>
                        </Box>
                      </Grid>

                      {/* Call Start Time */}
                      <Grid item xs={12}>
                        <Box display="flex" alignItems="center" gap={6}>
                          <Typography width={isMobile ? 100 : 200}>Call Start Time</Typography>
                          <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <Box flex={1} className="call-start-time-wrapper">
                              <Box className="call-start-time-picker">
                                <DatePickerComponent
                                  label="date"
                                  fieldName="callStartDate"
                                  value={values?.callStartDate || null}
                                  onChange={(date) =>
                                    setFieldValue("callStartDate", date)
                                  }
                                  minDate={dayjs()}
                                  optional={true}
                                  isLabel={false}
                                  disabled={initialCall?.id ? true : false}
                                />
                              </Box>
                              <Box className="call-start-time-picker">
                                <DesktopTimePicker
                                  label="Select Time"
                                  value={values.callStartTime}
                                  onChange={(newTime) =>
                                    setFieldValue("callStartTime", newTime)
                                  }
                                  minTime={getMinSelectableTime(values)}
                                  // shouldDisableTime={(timeValue, view) => {
                                  //   const minTime = getMinSelectableTime(values);

                                  //   if (dayjs(values.callStartDate).isSame(dayjs(), "day")) {
                                  //     if (view === "hours") {
                                  //       return timeValue.hour() < minTime.hour();
                                  //     }

                                  //     if (view === "minutes") {
                                  //       const selectedHour = values.callStartTime?.hour() ?? 0;
                                  //       const testTime = dayjs()
                                  //         .hour(selectedHour)
                                  //         .minute(timeValue.minute())
                                  //         .second(0);

                                  //       return testTime.isBefore(minTime);
                                  //     }
                                  //   }

                                  //   return false;
                                  // }}

                                  slotProps={{
                                    textField: {
                                      size: "small",
                                      error: Boolean(
                                        touched.callStartTime &&
                                          errors.callStartTime
                                      ),
                                      helperText:
                                        touched.callStartTime &&
                                        errors.callStartTime
                                          ? errors.callStartTime
                                          : " ",
                                      InputProps: {
                                        disableUnderline: true,
                                        style: {
                                          border: "none",
                                        },
                                      },
                                      sx: {
                                        ".MuiOutlinedInput-notchedOutline": {
                                          border: "none", // remove border
                                        },
                                        ".MuiInputBase-input": {
                                          padding: 0,
                                        },
                                        // Hide label *only when floating/shrink*
                                        "& label.MuiInputLabel-shrink": {
                                          display: "none",
                                        },
                                      },
                                    },
                                  }}
                                  disabled={initialCall?.id ? true : false}
                                />
                              </Box>
                              {initialCall?.id && (
                                <Box className="lock-icon-wrapper">
                                  <img
                                    src={lockIcon}
                                    alt="lock icon"
                                    style={{ width: 20, height: 20 }}
                                  />
                                </Box>
                              )}
                            </Box>
                          </LocalizationProvider>
                        </Box>
                      </Grid>

                      {/* Call Owner */}
                      <Grid item xs={12}>
                        <Box display="flex" alignItems="center" gap={6}>
                          <Typography width={isMobile ? 100 : 200}>Call Owner</Typography>
                          <Box flex={1}>
                            <SearchableSelectOwner
                              fieldName="callOwner"
                              options={leadOptions}
                              isShowError={!values?.callOwner}
                              placeholder="Select Owner"
                              optional={true}
                              value={
                                leadOptions.find(
                                  (opt) => opt.value === values?.callOwner
                                ) || null
                              }
                              onChange={(
                                selectedOption: OwnerOption | null
                              ) => {
                                if (selectedOption) {
                                  setFieldValue(
                                    "callOwner",
                                    selectedOption.value
                                  ); // saving only the ID
                                } else {
                                  setFieldValue("callOwner", null); // handle null case
                                }
                              }}
                              handleBlur={() => {
                                setFieldTouched("callOwner", true);
                              }}
                            />
                          </Box>
                        </Box>
                      </Grid>

                      {/* Subject */}
                      <Grid item xs={12}>
                        <Box display="flex" alignItems="center" gap={6}>
                          <Typography width={isMobile ? 100 : 200}>Subject</Typography>
                          <Box flex={1}>
                            <TextInputComponent
                              type="text"
                              fieldName="subject"
                              placeholder="Add subject"
                              optional={false}
                              disabled={false}
                              value={values.subject}
                              onChange={handleChange}
                              maxlength="200"
                            />
                          </Box>
                        </Box>
                      </Grid>
                    </Grid>

                    <Typography className="call-purpose-header">
                      Purpose Of Outgoing Call
                    </Typography>
                    <Grid container spacing={2}>
                      {/* Call Purpose */}
                      <Grid item xs={12}>
                        <Box display="flex" alignItems="center" gap={6}>
                          <Typography width={isMobile ? 100 : 200}>Call Purpose</Typography>
                          <Box flex={1}>
                            <SelectInputComponent
                              fieldName="priority"
                              value={values?.callPurpose || "None"}
                              onChange={(e: any) => {
                                setFieldValue("callPurpose", e);
                              }}
                              options={CALL_PURPOSE_OPTIONS}
                              optional={true}
                              isShowError={!values.callPurpose}
                              placeholder="Select Call Purpose"
                            />
                          </Box>
                        </Box>
                      </Grid>
                      {/* Call Agenda */}
                      <Grid item xs={12}>
                        <Box display="flex" alignItems="center" gap={6}>
                          <Typography width={isMobile ? 100 : 200}>Call Agenda</Typography>
                          <Box flex={1}>
                            <TextInputComponent
                              type="text"
                              fieldName="callAgenda"
                              placeholder="Add call agenda"
                              optional={false}
                              disabled={false}
                              value={values.callAgenda}
                              onChange={handleChange}
                              maxlength="200"
                            />
                          </Box>
                        </Box>
                      </Grid>
                    </Grid>
                  </DialogContent>
                  <DialogActions sx={{ margin: "12px" }}>
                    <button type="button" onClick={onClose} className="cancel-button">
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="cancel-button next-button-wrapper"
                    >
                      Save
                    </button>
                  </DialogActions>
                </div>
              </Form>
            );
          }}
        </Formik>
        <div className="mark-completed">
          <ConfirmationPopup
            open={isConflictWithMeeting}
            onCancel={() => {
              setIsConflictWithMeeting(false);
              setConflictMessage("");
            }}
            onOkay={() => {
              if (pendingPayload) {
                const updatedPayload = {
                  ...pendingPayload,
                  ignoreOverlap: true, // ✅ Add this key when retrying
                };
                saveCall(updatedPayload);
                setIsConflictWithMeeting(false);
                setConflictMessage("");
                setPendingPayload(null);
              }
            }}
            title={conflictMessage}
            note="Are you sure you want to create this call anyway?"
            buttonText="Confirm"
            Icon={ConfirmInfo}
            className="mark-completed-confirmation"
          />
        </div>
      </Dialog>
      <AlertComponent
        message={alertMessage}
        type={alertType}
        open={alertOpen}
        onClose={handleAlertClose}
      />
    </>
  );
};

export default CommonCallDialog;
